import * as React from "react";
import { useState, useMemo, useCallback, useEffect } from "react";
import useFeature from "hooks/useFeature";
import { VALID_ACTIVE_SEARCH_STATUSES } from "data-lake/preview/Constants";
import usePluginEntities from "hooks/usePluginEntities";
import { INTERNAL_DATA_LAKE_ID } from "data-lake/preview/storage-types/internal/Constants";
import findDataLakeIdInSearchType from "data-lake/preview/utils/findDataLakeIdInSearchType";
import SelectedDataLakesContext from "data-lake/contexts/SelectedDataLakesContext";
import { INTERNAL_DATA_LAKE_INFO } from "data-lake/Constants";
const isValidDataLakeId = (activeDataLakes, dataLakeId) => activeDataLakes.some(({ id }) => id === dataLakeId);
const isValidQueryTypeForDataLake = (dataLakePlugins, type) => dataLakePlugins.some(({ queryType }) => queryType === type);
const initiallySelectedDataLakeId = (featureIsEnabled, initialUrlParams, activeSearch, searchJobStatus, activeDataLakes, dataLakePlugins) => {
  if (!featureIsEnabled) {
    return INTERNAL_DATA_LAKE_ID;
  }
  if ((initialUrlParams == null ? void 0 : initialUrlParams.dataLakeId) && isValidDataLakeId(activeDataLakes, initialUrlParams == null ? void 0 : initialUrlParams.dataLakeId)) {
    return initialUrlParams.dataLakeId;
  }
  if (VALID_ACTIVE_SEARCH_STATUSES.includes(searchJobStatus) && activeSearch) {
    const query = activeSearch.queries.first();
    const queryType = query.query.type;
    if (isValidQueryTypeForDataLake(dataLakePlugins, queryType)) {
      return findDataLakeIdInSearchType(activeSearch);
    }
  }
  if ((activeDataLakes == null ? void 0 : activeDataLakes.length) > 0) {
    if (activeDataLakes.find(({ id: dataLakeId }) => dataLakeId === INTERNAL_DATA_LAKE_ID)) {
      return INTERNAL_DATA_LAKE_ID;
    }
    return activeDataLakes[0].id;
  }
  throw new Error("Expected at least one data lake to be configured.");
};
const PreviewConfiguredDataLakesProvider = ({
  children = void 0,
  initialUrlParams,
  activeSearch,
  searchJobStatus,
  activeDataLakes,
  setDataLakeIdUrlParam
}) => {
  const featureIsEnabled = useFeature("external_data_lake_search");
  const dataLakePlugins = usePluginEntities("dataLakeStorageTypes");
  const [selectedDataLakeId, selectDataLakeId] = useState(
    initiallySelectedDataLakeId(
      featureIsEnabled,
      initialUrlParams,
      activeSearch,
      searchJobStatus,
      activeDataLakes,
      dataLakePlugins
    )
  );
  const [dataLakeFieldsFetchQuery, setDataLakeFieldsFetchQuery] = useState(null);
  const selectDataLake = useCallback(
    (newDataLakeId) => {
      setDataLakeFieldsFetchQuery(null);
      setDataLakeIdUrlParam(newDataLakeId, true);
      return selectDataLakeId(newDataLakeId);
    },
    [setDataLakeIdUrlParam]
  );
  useEffect(() => {
    setDataLakeIdUrlParam(selectedDataLakeId);
  }, [selectedDataLakeId, setDataLakeIdUrlParam]);
  const contextValue = useMemo(
    () => {
      var _a;
      return {
        selectDataLake,
        selectedDataLake: (_a = activeDataLakes.find(({ id }) => id === selectedDataLakeId)) != null ? _a : INTERNAL_DATA_LAKE_INFO,
        dataLakeFieldsFetchQuery,
        setDataLakeFieldsFetchQuery
      };
    },
    [activeDataLakes, dataLakeFieldsFetchQuery, selectDataLake, selectedDataLakeId]
  );
  return /* @__PURE__ */ React.createElement(SelectedDataLakesContext.Provider, { value: contextValue }, children);
};
export default PreviewConfiguredDataLakesProvider;
